import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Input } from './ui/input';
import { ChevronDown, MapPin, DoorOpen } from 'lucide-react';

/**
 * Combobox per Camera/Area di un hotel.
 * - Mostra in dropdown le camere e aree comuni dell'hotel selezionato
 * - L'utente può anche digitare liberamente un valore non presente nella lista
 * - Filtra le opzioni mentre si digita
 *
 * Props:
 *   value            string                                — valore corrente
 *   onChange         (newValue: string) => void
 *   rooms            string[]                              — camere
 *   commonAreas      string[]                              — aree comuni
 *   disabled         boolean                               — disabilita input
 *   placeholder      string
 *   testId           string                                — data-testid del wrapper
 *   inputTestId      string                                — data-testid dell'input
 *   className        string                                — classi aggiuntive sull'input
 */
const AreaCombobox = ({
  value,
  onChange,
  rooms = [],
  commonAreas = [],
  disabled = false,
  placeholder = 'Digita o seleziona...',
  testId = 'area-combobox',
  inputTestId = 'area-combobox-input',
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const wrapperRef = useRef(null);
  const listRef = useRef(null);

  // Filter options based on current value
  const filtered = useMemo(() => {
    const q = (value || '').trim().toLowerCase();
    const matchRooms = q
      ? rooms.filter(r => String(r).toLowerCase().includes(q))
      : rooms;
    const matchAreas = q
      ? commonAreas.filter(a => String(a).toLowerCase().includes(q))
      : commonAreas;
    return { matchRooms, matchAreas, total: matchRooms.length + matchAreas.length };
  }, [value, rooms, commonAreas]);

  // Close on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setHighlight(-1);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // Flat list for keyboard navigation
  const flatOptions = useMemo(
    () => [...filtered.matchAreas, ...filtered.matchRooms],
    [filtered]
  );

  const selectOption = (val) => {
    onChange(val);
    setOpen(false);
    setHighlight(-1);
  };

  const handleKeyDown = (e) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      setOpen(true);
      return;
    }
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight(prev => Math.min(prev + 1, flatOptions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      if (highlight >= 0 && flatOptions[highlight]) {
        e.preventDefault();
        selectOption(flatOptions[highlight]);
      } else {
        setOpen(false);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
      setHighlight(-1);
    }
  };

  const hasAnyOption = (rooms.length + commonAreas.length) > 0;

  return (
    <div className="relative" ref={wrapperRef} data-testid={testId}>
      <div className="relative">
        <Input
          value={value || ''}
          onChange={(e) => { onChange(e.target.value); if (!open) setOpen(true); }}
          onFocus={() => hasAnyOption && setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          className={`${className} pr-9`}
          data-testid={inputTestId}
        />
        {hasAnyOption && (
          <button
            type="button"
            onMouseDown={(e) => { e.preventDefault(); setOpen(o => !o); }}
            disabled={disabled}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700 disabled:opacity-40"
            data-testid={`${testId}-toggle`}
            aria-label="Mostra suggerimenti"
            tabIndex={-1}
          >
            <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>
        )}
      </div>

      {open && hasAnyOption && (
        <div
          ref={listRef}
          className="absolute z-50 mt-1 w-full max-h-72 overflow-auto rounded-md border border-slate-200 bg-white shadow-lg dark:bg-slate-900 dark:border-slate-700"
          data-testid={`${testId}-list`}
        >
          {filtered.total === 0 ? (
            <div className="px-3 py-3 text-xs text-slate-500">
              Nessun risultato. Usa il testo digitato &laquo;<b>{value}</b>&raquo;.
            </div>
          ) : (
            <>
              {filtered.matchAreas.length > 0 && (
                <div className="py-1">
                  <div className="px-3 py-1 text-[10px] font-bold uppercase text-slate-400 tracking-wider">
                    Aree Comuni
                  </div>
                  {filtered.matchAreas.map((a, idx) => {
                    const globalIdx = idx;
                    const active = globalIdx === highlight;
                    return (
                      <button
                        key={`a-${a}`}
                        type="button"
                        onMouseDown={(e) => { e.preventDefault(); selectOption(a); }}
                        onMouseEnter={() => setHighlight(globalIdx)}
                        className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 ${
                          active
                            ? 'bg-blue-50 dark:bg-blue-950 text-blue-900 dark:text-blue-200'
                            : 'hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-800 dark:text-slate-200'
                        }`}
                        data-testid={`${testId}-option-area-${a}`}
                      >
                        <MapPin className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                        <span>{a}</span>
                      </button>
                    );
                  })}
                </div>
              )}
              {filtered.matchRooms.length > 0 && (
                <div className="py-1 border-t border-slate-100 dark:border-slate-800">
                  <div className="px-3 py-1 text-[10px] font-bold uppercase text-slate-400 tracking-wider">
                    Camere
                  </div>
                  {filtered.matchRooms.map((r, idx) => {
                    const globalIdx = filtered.matchAreas.length + idx;
                    const active = globalIdx === highlight;
                    return (
                      <button
                        key={`r-${r}`}
                        type="button"
                        onMouseDown={(e) => { e.preventDefault(); selectOption(r); }}
                        onMouseEnter={() => setHighlight(globalIdx)}
                        className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 ${
                          active
                            ? 'bg-blue-50 dark:bg-blue-950 text-blue-900 dark:text-blue-200'
                            : 'hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-800 dark:text-slate-200'
                        }`}
                        data-testid={`${testId}-option-room-${r}`}
                      >
                        <DoorOpen className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                        <span>{r}</span>
                      </button>
                    );
                  })}
                </div>
              )}
              <div className="px-3 py-2 border-t border-slate-100 dark:border-slate-800 text-[10px] text-slate-400">
                Puoi anche digitare un valore personalizzato.
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AreaCombobox;
